import {
  Modal,
  Button,
  message,
  List,
  Typography,
  Input,
  Checkbox,
  Spin,
  Empty,
  Divider,
  Pagination,
  Tabs
} from 'antd'
import {
  UploadOutlined,
  FolderOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  SearchOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import React, { useState, useEffect } from 'react'
import { api } from '../../../../../../../../../services'
import _, { set } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import actions from '../../../../../../../../../store/actions'

import {
  cleanTextContent,
  getContent,
  updateContentWithRevisedData
} from './contentHelper'
import { useTranslation } from 'react-i18next'

const { Text, Title } = Typography

const ACCEPTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '.docx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    '.pptx',
  'text/plain': '.txt'
}

const AddContent = ({
  onCancel,
  onSelect,
  visible = true,
  previewData,
  lessonTitle,
  lessonUpdate,
  language
}) => {
  const [step, setStep] = useState('select') // 'select', 'upload', 'auto-library', 'manual-library'
  const [fileList, setFileList] = useState([])
  const [fileError, setFileError] = useState('')

  // Library states
  const [isFetchingFiles, setIsFetchingFiles] = useState(false)
  const [libraryFiles, setLibraryFiles] = useState([])
  const [selectedLibraryFiles, setSelectedLibraryFiles] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(15)
  const [totalItems, setTotalItems] = useState(0)
  const [activeLibraryTab, setActiveLibraryTab] = useState('myLibrary')
  const [isGeneratingContent, setIsGeneratingContent] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [fileUploadStatus, setFileUploadStatus] = useState({})
  const [pendingRevisedContent, setPendingRevisedContent] = useState(null)
  const [lastStep, setLastStep] = useState('select')
  const [headingPageMap, setHeadingPageMap] = useState({})

  const user = useSelector(state => state.user)
  const courseTitle = useSelector(state => state.courseBuilder.courseTitle)
  const library = useSelector(state => state.library)
  const dispatch = useDispatch()

  const uploadProgress =
    library && library.uploadProgress ? library.uploadProgress : []

  const { t } = useTranslation()

  useEffect(() => {
    checkFileError()
  }, [fileList])

  useEffect(() => {
    // Reset when step changes
    setFileList([])
    setSelectedLibraryFiles([])
    setFileError('')
    setSearchValue('')
  }, [step])

  useEffect(() => {
    if (step === 'manual-library') {
      getAllFiles()
    }
  }, [step, searchValue, activeLibraryTab])

  const handleSearchDebounced = _.debounce(value => {
    setPage(1)
    setSearchValue(value)
  }, 800)

  const handleLibraryTabChange = key => {
    setActiveLibraryTab(key)
    setPage(1)
    setSelectedLibraryFiles([])
  }

  const getAllFiles = async (customPage, customPageSize) => {
    setIsFetchingFiles(true)
    try {
      const currentPage = typeof customPage === 'number' ? customPage : page
      const currentPageSize =
        typeof customPageSize === 'number' ? customPageSize : pageSize

      // Prepare API parameters based on active tab
      const apiParams = {
        limit: currentPageSize,
        onlyMedia: true,
        offset: (currentPage - 1) * currentPageSize,
        search: searchValue,
        sort: 'NEWEST',
        type: 'PDF,DOC,PPT,TEXT',
        parent_id: null,
        is_ai_enabled: true
      }

      // Add appropriate parameter based on active tab
      if (activeLibraryTab === 'myLibrary') {
        apiParams.isActiveOrg = true
      } else if (activeLibraryTab === 'orgLibrary') {
        apiParams.isOrg = true
      }

      const response = await api.library.getLibraryList(apiParams)
      const rows = response.data.rows || []
      const count = response.data.count || 0
      setLibraryFiles(rows)
      setTotalItems(count)
    } catch (error) {
      console.error('Error fetching files:', error)
      message.error(t('v5:failed_to_load_documents'))
    } finally {
      setIsFetchingFiles(false)
    }
  }

  const checkFileError = () => {
    if (step === 'upload') {
      if (fileList.length === 0) {
        setFileError(t('v5:please_upload_at_last_one_supported_file'))
      } else if (fileList.length > 5) {
        setFileError(t('v5:you_can_upload_a_maximum_of_files'))
      } else {
        setFileError('')
      }
    }
  }

  const handleFileChange = e => {
    const files = Array.from(e.target.files)
    let validFiles = []

    files.forEach(file => {
      const isAcceptedType = Object.keys(ACCEPTED_FILE_TYPES).includes(
        file.type
      )

      if (!isAcceptedType) {
        const acceptedExtensions = Object.values(ACCEPTED_FILE_TYPES).join(', ')
        message.error(
          `${file.name} ${t('v5:file_not_supported')} ${acceptedExtensions}`
        )
      } else {
        validFiles.push({
          uid: `${Date.now()}-${file.name}`,
          name: file.name,
          size: file.size,
          type: file.type,
          originFileObj: file
        })
      }
    })

    setFileList([...fileList, ...validFiles])
  }

  const handleLibraryFileSelect = file => {
    const isSelected = selectedLibraryFiles.some(f => f.id === file.id)
    if (isSelected) {
      setSelectedLibraryFiles(
        selectedLibraryFiles.filter(f => f.id !== file.id)
      )
    } else {
      setSelectedLibraryFiles([...selectedLibraryFiles, file])
    }
  }

  const handleRemoveFile = uid => {
    setFileList(fileList.filter(f => f.uid !== uid))
  }

  const handleOk = async () => {
    if (step === 'upload') {
      if (fileList.length === 0) {
        message.warning('Please upload at least one file')
        return
      }
      if (fileError) {
        message.error(fileError)
        return
      }
      try {
        setIsUploading(true)
        setLastStep('upload')
        const uploaded = await uploadFiles(fileList)
        await generateContent({ mode: 'upload', files: uploaded })
      } catch (e) {
        // error messages handled in uploadFiles
      } finally {
        setIsUploading(false)
      }
    } else if (step === 'auto-library') {
      setLastStep('auto-library')
      await generateContent({ mode: 'auto-library', files: [] })
    } else if (step === 'manual-library') {
      if (selectedLibraryFiles.length === 0) {
        message.warning('Please select at least one document')
        return
      }
      setLastStep('manual-library')
      await generateContent({
        mode: 'manual-library',
        files: selectedLibraryFiles
      })
    }
    // handleCancel()
  }

  const handleCancel = () => {
    setStep('select')
    setFileList([])
    setSelectedLibraryFiles([])
    setFileError('')
    setSearchValue('')
    onCancel && onCancel()
  }

  const getFileIcon = mimeType => {
    if (!mimeType) return '#8c8c8c'
    if (mimeType.includes('pdf')) return '#ff4757'
    if (mimeType.includes('word') || mimeType.includes('msword'))
      return '#2b7de9'
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation'))
      return '#ff6b35'
    if (mimeType.includes('text')) return '#5f27cd'
    return '#8c8c8c'
  }

  const generateContent = async ({ mode, files: selectedFiles }) => {
    try {
      setIsGeneratingContent(true)

      const { contentObj, headingPageMap } = getContent(previewData)

      const payload = {
        course_name: courseTitle,
        topic_name: lessonTitle,
        content: contentObj,
        org_id: user?.info?.active_organization_id,
        file_list: selectedFiles.map(file => file.path),
        language,
        ...(mode === 'upload' && { isUpload: true })
      }

      const response = await api.courses.curateContent(payload)

      const newContentObject =
        response && response.data && response.data.revised_content

      if (newContentObject) {
        setPendingRevisedContent(newContentObject)
        setStep('preview')
        setHeadingPageMap(headingPageMap)
      }
    } catch (error) {
      console.error('Error generating content:', error)
      message.error('Failed to generate content. Please try again.')
    } finally {
      setIsGeneratingContent(false)
    }
  }

  const handleConfirmApply = () => {
    if (!pendingRevisedContent) return
    const newPagedata = updateContentWithRevisedData(
      previewData,
      pendingRevisedContent,
      headingPageMap
    )
    if (newPagedata) {
      updatePageData(newPagedata)
    }
    setPendingRevisedContent(null)
    setLastStep('select')
  }

  const handleCancelPreview = () => {
    setPendingRevisedContent(null)
    setStep(lastStep)
  }

  const updatePageData = newPageData => {
    dispatch(
      actions.lessonBuilder.handleLessonInput('pages_landscape', newPageData)
    )
    lessonUpdate({ pages_landScape: newPageData })

    handleCancel()
  }

  const uploadFiles = async files => {
    const results = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        setFileUploadStatus(function (prevStatus) {
          return Object.assign({}, prevStatus, { [file.name]: 'uploading' })
        })
        const res = await dispatch(
          actions.library.upload(file.originFileObj, {
            ai_language: 'en-US',
            isAiEnabled: true
          })
        )
        results.push(res)
        setFileUploadStatus(function (prevStatus) {
          return Object.assign({}, prevStatus, { [file.name]: 'success' })
        })
      } catch (error) {
        console.error('Error uploading file:', file && file.name, error)
        message.error('Failed to upload ' + (file && file.name))
        setFileUploadStatus(function (prevStatus) {
          return Object.assign({}, prevStatus, { [file.name]: 'error' })
        })
        throw error
      }
    }
    return results
  }

  const getProgressPercentFor = file => {
    if (!uploadProgress || !uploadProgress.length) return null
    const byUid = uploadProgress.find(function (p) {
      return p && p.uid === file.uid
    })
    if (byUid && typeof byUid.percent === 'number')
      return Math.round(byUid.percent)
    const byName = uploadProgress.find(function (p) {
      return p && p.name === file.name
    })
    if (byName && typeof byName.percent === 'number')
      return Math.round(byName.percent)
    return null
  }

  // Selection Step
  if (step === 'select') {
    return (
      <Modal
        title={
          <Text strong style={{ fontSize: 16 }}>
            {t('v4:add_content')}
          </Text>
        }
        visible={visible}
        onCancel={handleCancel}
        centered
        width={520}
        maskClosable={false}
        footer={null}
      >
        <div style={{ padding: '16px 0' }}>
          <Text style={{ color: '#595959', fontSize: 14 }}>
            {t('v4:choose_your_content_source')}
          </Text>

          <div
            style={{
              marginTop: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
          >
            {/* Upload Option */}
            <div
              onClick={() => setStep('upload')}
              style={{
                border: '1px solid #d9d9d9',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: 2
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.borderColor = '#1890ff')
              }
              onMouseLeave={e =>
                (e.currentTarget.style.borderColor = '#d9d9d9')
              }
            >
              <div
                style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}
              >
                <CloudUploadOutlined
                  style={{ fontSize: 24, color: '#1890ff', marginTop: 2 }}
                />
                <div style={{ flex: 1 }}>
                  <Text
                    strong
                    style={{ fontSize: 15, display: 'block', marginBottom: 4 }}
                  >
                    {t('v4:upload_files')}
                  </Text>
                  <Text
                    style={{ fontSize: 13, color: '#8c8c8c', lineHeight: 1.5 }}
                  >
                    {t('v4:upload_new_document_from_your_device')}
                  </Text>
                </div>
              </div>
            </div>

            {/* Auto Library Option */}
            <div
              onClick={() => setStep('auto-library')}
              style={{
                border: '1px solid #d9d9d9',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: 2
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.borderColor = '#1890ff')
              }
              onMouseLeave={e =>
                (e.currentTarget.style.borderColor = '#d9d9d9')
              }
            >
              <div
                style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}
              >
                <DatabaseOutlined
                  style={{ fontSize: 24, color: '#52c41a', marginTop: 2 }}
                />
                <div style={{ flex: 1 }}>
                  <Text
                    strong
                    style={{ fontSize: 15, display: 'block', marginBottom: 4 }}
                  >
                    {t('v4:automatic_library_selection')}
                  </Text>
                  <Text
                    style={{ fontSize: 13, color: '#8c8c8c', lineHeight: 1.5 }}
                  >
                    {t('v4:system_automatically_selects_relevant_document')}
                  </Text>
                </div>
              </div>
            </div>

            {/* Manual Library Option */}
            <div
              onClick={() => setStep('manual-library')}
              style={{
                border: '1px solid #d9d9d9',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: 2
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.borderColor = '#1890ff')
              }
              onMouseLeave={e =>
                (e.currentTarget.style.borderColor = '#d9d9d9')
              }
            >
              <div
                style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}
              >
                <FolderOutlined
                  style={{ fontSize: 24, color: '#faad14', marginTop: 2 }}
                />
                <div style={{ flex: 1 }}>
                  <Text
                    strong
                    style={{ fontSize: 15, display: 'block', marginBottom: 4 }}
                  >
                    {t('v4:manual_library_selection')}
                  </Text>
                  <Text
                    style={{ fontSize: 13, color: '#8c8c8c', lineHeight: 1.5 }}
                  >
                    {t('v4:choose_specific_documents_from_your_library')}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  // Upload Step
  if (step === 'upload') {
    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CloudUploadOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ fontSize: 16 }}>
              {t('v4:upload_files')}
            </Text>
          </div>
        }
        visible={visible}
        onCancel={handleCancel}
        centered
        width={560}
        maskClosable={false}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button onClick={() => setStep('select')}>
              {t('buttons:back')}
            </Button>
            <div>
              <Button
                type="primary"
                onClick={handleOk}
                loading={isUploading || isGeneratingContent}
                disabled={fileList.length === 0 || !!fileError}
              >
                {t('v4:generate_content')}
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ padding: '8px 0' }}>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="file-upload-input"
          />

          <label htmlFor="file-upload-input">
            <div
              style={{
                border: '2px dashed #d9d9d9',
                padding: '32px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: 2
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.borderColor = '#1890ff')
              }
              onMouseLeave={e =>
                (e.currentTarget.style.borderColor = '#d9d9d9')
              }
            >
              <UploadOutlined
                style={{
                  fontSize: 48,
                  color: '#d9d9d9',
                  display: 'block',
                  margin: '0 auto 16px'
                }}
              />
              <Text
                style={{
                  fontSize: 14,
                  color: '#262626',
                  display: 'block',
                  marginBottom: 4
                }}
              >
                {t('v5:click_to_select_files')}
              </Text>
              <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                PDF, DOC, DOCX, PPT, PPTX, TXT (Max 5 files)
              </Text>
            </div>
          </label>

          {fileError && (
            <div
              style={{
                marginTop: 16,
                padding: '8px 12px',
                border: '1px solid #ff4d4f',
                color: '#ff4d4f',
                fontSize: 13,
                borderRadius: 2
              }}
            >
              {fileError}
            </div>
          )}

          {fileList.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <Text
                strong
                style={{
                  fontSize: 13,
                  color: '#262626',
                  display: 'block',
                  marginBottom: 12
                }}
              >
                {t('v5:selected_files')} ({fileList.length}/5)
              </Text>
              <div
                style={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  border: '1px solid #f0f0f0'
                }}
              >
                {fileList.map(file => (
                  <div
                    key={file.uid}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <FileTextOutlined
                        style={{ fontSize: 16, color: '#8c8c8c' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{
                            fontSize: 13,
                            color: '#262626',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {file.name}
                        </Text>
                        <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                          {(file.size / 1024).toFixed(2)} KB
                        </Text>
                        {fileUploadStatus[file.name] === 'uploading' && (
                          <div style={{ marginTop: 6 }}>
                            {(function () {
                              var percent = getProgressPercentFor(file)
                              if (typeof percent === 'number') {
                                return (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8
                                    }}
                                  >
                                    <div
                                      style={{
                                        flex: 1,
                                        height: 6,
                                        background: '#f0f0f0'
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: '100%',
                                          width: percent + '%',
                                          background: '#1890ff',
                                          transition: 'width 0.2s'
                                        }}
                                      />
                                    </div>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        color: '#8c8c8c',
                                        minWidth: 32
                                      }}
                                    >
                                      {percent}%
                                    </span>
                                  </div>
                                )
                              }
                              return (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                  }}
                                >
                                  <Spin size="small" />
                                  <span
                                    style={{ fontSize: 12, color: '#8c8c8c' }}
                                  >
                                    {t('general:uploading')}
                                  </span>
                                </div>
                              )
                            })()}
                          </div>
                        )}
                        {fileUploadStatus[file.name] === 'success' && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 12,
                              color: '#52c41a'
                            }}
                          >
                            {t('v5:uploaded')}
                          </div>
                        )}
                        {fileUploadStatus[file.name] === 'error' && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 12,
                              color: '#ff4d4f'
                            }}
                          >
                            {t('v5:failed')}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      type="link"
                      size="small"
                      danger
                      onClick={() => handleRemoveFile(file.uid)}
                      style={{ padding: '0 8px' }}
                    >
                      {t('v2:remove')}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    )
  }

  // Auto Library Step
  if (step === 'auto-library') {
    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DatabaseOutlined style={{ color: '#52c41a' }} />
            <Text strong style={{ fontSize: 16 }}>
              {t('v4:automatic_library_selection')}
            </Text>
          </div>
        }
        visible={visible}
        onCancel={handleCancel}
        centered
        width={520}
        maskClosable={false}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button onClick={() => setStep('select')}>{t('v4:back')}</Button>
            <div>
              <Button
                type="primary"
                onClick={handleOk}
                loading={isGeneratingContent}
              >
                {t('v4:add_to_current_page')}
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ padding: '24px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <DatabaseOutlined
              style={{ fontSize: 56, color: '#52c41a', marginBottom: 24 }}
            />
            <Text
              style={{
                fontSize: 14,
                color: '#595959',
                display: 'block',
                lineHeight: 1.6
              }}
            >
              {t(
                'v5:system_will_automatically_analyze_and_select_the_most_relevant'
              )}
            </Text>
          </div>
        </div>
      </Modal>
    )
  }

  // Manual Library Step
  if (step === 'manual-library') {
    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOutlined style={{ color: '#faad14' }} />
            <Text strong style={{ fontSize: 16 }}>
              {t('v5:select_from_library')}
            </Text>
          </div>
        }
        visible={visible}
        onCancel={handleCancel}
        centered
        width={700}
        maskClosable={false}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button onClick={() => setStep('select')}>
              {t('buttons:back')}
            </Button>
            <div>
              <Button
                type="primary"
                onClick={handleOk}
                disabled={selectedLibraryFiles.length === 0}
                loading={isGeneratingContent}
              >
                {`${t('v5:generate_content_from')} ${selectedLibraryFiles.length
                  } Document${selectedLibraryFiles.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        }
      >
        <div>
          <Tabs activeKey={activeLibraryTab} onChange={handleLibraryTabChange}>
            <Tabs.TabPane tab={t('v5:my_library')} key="myLibrary" />
            <Tabs.TabPane tab={t('v5:org_library')} key="orgLibrary" />
          </Tabs>

          <Input
            placeholder={t('v5:search_document')}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
            onChange={e => handleSearchDebounced(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          {selectedLibraryFiles.length > 0 && (
            <div
              style={{
                padding: '12px',
                border: '1px solid #e8e8e8',
                marginBottom: 16,
                borderRadius: 2
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8
                }}
              >
                <Text strong style={{ fontSize: 13 }}>
                  {selectedLibraryFiles.length} {t('v5:selected')}
                </Text>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setSelectedLibraryFiles([])}
                  style={{ padding: 0 }}
                >
                  {t('buttons:clear')}
                </Button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedLibraryFiles.map(file => (
                  <div
                    key={file.id}
                    style={{
                      padding: '4px 10px',
                      border: '1px solid #d9d9d9',
                      fontSize: 12,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <span
                      style={{
                        maxWidth: 150,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {file.title || file.filename || file.name}
                    </span>
                    <span
                      onClick={() => handleLibraryFileSelect(file)}
                      style={{
                        cursor: 'pointer',
                        color: '#8c8c8c',
                        fontSize: 10
                      }}
                    >
                      ✕
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isFetchingFiles ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <Spin />
              <Text
                style={{
                  display: 'block',
                  marginTop: 12,
                  color: '#8c8c8c',
                  fontSize: 13
                }}
              >
                {t('v5:loading_documents')}
              </Text>
            </div>
          ) : libraryFiles.length === 0 ? (
            <Empty
              description="No documents found"
              style={{ padding: '48px 0' }}
            />
          ) : (
            <div
              style={{
                maxHeight: 400,
                overflowY: 'auto',
                border: '1px solid #f0f0f0'
              }}
            >
              {libraryFiles.map((file, index) => {
                const isSelected = selectedLibraryFiles.some(
                  f => f.id === file.id
                )
                const iconColor = getFileIcon(file.media_mime_type)

                return (
                  <div
                    key={file.id}
                    onClick={() => handleLibraryFileSelect(file)}
                    style={{
                      padding: '12px 16px',
                      borderBottom:
                        index === libraryFiles.length - 1
                          ? 'none'
                          : '1px solid #f0f0f0',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      backgroundColor: isSelected ? '#fafafa' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={e => {
                      if (!isSelected)
                        e.currentTarget.style.backgroundColor = '#fafafa'
                    }}
                    onMouseLeave={e => {
                      if (!isSelected)
                        e.currentTarget.style.backgroundColor = '#fff'
                    }}
                  >
                    <Checkbox checked={isSelected} />
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        border: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FileTextOutlined
                        style={{ fontSize: 16, color: iconColor }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        strong
                        style={{
                          fontSize: 13,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {file.title || file.filename || file.name}
                      </Text>
                      {file.media_mime_type && (
                        <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                          {file.media_mime_type.split('/')[1]
                            ? file.media_mime_type.split('/')[1].toUpperCase()
                            : 'Document'}
                        </Text>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {totalItems > 0 && (
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={totalItems}
                onChange={(nextPage, nextPageSize) => {
                  setSelectedLibraryFiles([])
                  setPage(nextPage)
                  setPageSize(nextPageSize)
                  getAllFiles(nextPage, nextPageSize)
                }}
              />
            </div>
          )}
        </div>
      </Modal>
    )
  }

  // Preview Step
  if (step === 'preview') {
    const selectedPages = Array.isArray(previewData)
      ? previewData.filter(function (page) {
        return page && page.pageSelected
      })
      : []
    const revisedEntries = pendingRevisedContent
      ? Object.keys(pendingRevisedContent).map(function (key) {
        return { heading: key, content: pendingRevisedContent[key] }
      })
      : []

    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ fontSize: 16 }}>
              {t('v5:review_generated_content')}
            </Text>
          </div>
        }
        visible={visible}
        onCancel={handleCancelPreview}
        centered
        width={720}
        maskClosable={false}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button onClick={handleCancelPreview}>{t('buttons:back')}</Button>
            <div>
              <Button type="primary" onClick={handleConfirmApply}>
                {t('v5:confirm_and_replace_content')}
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ padding: '8px 0' }}>
          <div
            style={{
              padding: '12px',
              border: '1px solid #faad14',
              background: '#fffbe6',
              color: '#ad6800',
              fontSize: 13,
              borderRadius: 2,
              marginBottom: 16
            }}
          >
            {t('v5:the_generate_content_will_replace')}
          </div>

          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 13, display: 'block' }}>
              {t('v5:pages_to_be_updated')}({selectedPages.length})
            </Text>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 8
              }}
            >
              {selectedPages.map(function (page, idx) {
                var headingEl =
                  page &&
                  page.data &&
                  page.data.find(function (el) {
                    return el && el.type === 'TEXT' && el.display === 'HEADING'
                  })
                var headingText =
                  headingEl && headingEl.params && headingEl.params.content
                    ? cleanTextContent(headingEl.params.content)
                    : 'Untitled'
                return (
                  <div
                    key={idx}
                    style={{
                      padding: '4px 10px',
                      border: '1px solid #d9d9d9',
                      fontSize: 12,
                      borderRadius: 2
                    }}
                  >
                    {headingText}
                  </div>
                )
              })}
            </div>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <Text
            strong
            style={{ fontSize: 13, display: 'block', marginBottom: 8 }}
          >
            {t('v5:generated_revisions')}
          </Text>
          <div
            style={{
              maxHeight: 360,
              overflowY: 'auto',
              border: '1px solid #f0f0f0'
            }}
          >
            {revisedEntries.length === 0 ? (
              <Empty
                description="No generated content to preview"
                style={{ padding: '48px 0' }}
              />
            ) : (
              revisedEntries.map(function (item, idx) {
                return (
                  <div
                    key={idx}
                    style={{
                      padding: '12px 16px',
                      borderBottom:
                        idx === revisedEntries.length - 1
                          ? 'none'
                          : '1px solid #f0f0f0'
                    }}
                  >
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                      {item.heading}
                    </Text>
                    <div
                      style={{
                        fontSize: 13,
                        color: '#595959',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {item.content}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </Modal>
    )
  }

  return null
}

export default AddContent
